import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ColumnMode } from '@siemens/ngx-datatable';
import { CurrencyDto } from '../../models/CurrencyDto';
import { AdminService } from '../admin.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from '../../services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TableColumn } from '@siemens/ngx-datatable';
import { CreateCurrencyPairComponent } from './create-currency-pair/create-currency-pair.component';
import { UtilsService } from '../../services/UtilsService';

@Component({
  selector: 'app-sheet',
  templateUrl: './sheet.component.html',
  styleUrls: ['./sheet.component.css']
})
export class SheetComponent implements OnInit {

  @ViewChild('sixLetterCodeHeaderTemplate', { static: true }) sixLetterCodeHeaderTemplate: TemplateRef<any>;
  @ViewChild('swapPriceFactorHeaderTemplate', { static: true }) swapPriceFactorHeaderTemplate: TemplateRef<any>;
  @ViewChild('spotPriceFactorHeaderTemplate', { static: true }) spotPriceFactorHeaderTemplate: TemplateRef<any>;
  @ViewChild('spotPricePrecisionHeaderTemplate', { static: true }) spotPricePrecisionHeaderTemplate: TemplateRef<any>;
  @ViewChild('swapPricePrecisionHeaderTemplate', { static: true }) swapPricePrecisionHeaderTemplate: TemplateRef<any>;
  @ViewChild('displaySpotPricePrecisionHeaderTemplate', { static: true }) displaySpotPricePrecisionHeaderTemplate: TemplateRef<any>;
  @ViewChild('roundingHeaderTemplate', { static: true }) roundingHeaderTemplate: TemplateRef<any>;
  @ViewChild('sixLetterCodeColumnTemplate', { static: true }) sixLetterCodeColumnTemplate: TemplateRef<any>;
  @ViewChild('swapPriceFactorColumnTemplate', { static: true }) swapPriceFactorColumnTemplate: TemplateRef<any>;
  @ViewChild('spotPriceFactorColumnTemplate', { static: true }) spotPriceFactorColumnTemplate: TemplateRef<any>;
  @ViewChild('spotPricePrecisionColumnTemplate', { static: true }) spotPricePrecisionColumnTemplate: TemplateRef<any>;
  @ViewChild('swapPricePrecisionColumnTemplate', { static: true }) swapPricePrecisionColumnTemplate: TemplateRef<any>;
  @ViewChild('displaySpotPricePrecisionColumnTemplate', { static: true }) displaySpotPricePrecisionColumnTemplate: TemplateRef<any>;
  @ViewChild('roundingColumnTemplate', { static: true }) roundingColumnTemplate: TemplateRef<any>;

  bit: boolean = true;
  restCallInProgress: boolean;

  currencyPairs: Array<CurrencyDto>;
  updateForm: FormGroup;
  columns: Array<TableColumn>;
  ColumnMode = ColumnMode;

  headerLabels = [
    'Currency pair',
    'Spot factor',
    'Swap factor',
    'Real spot precision',
    'Display spot precision',
    'Real swap precision',
    'Rounding'
  ];

  constructor(private readonly adminService: AdminService,
              private readonly modalService: BsModalService,
              private readonly fb: FormBuilder,
              private readonly snackBar: MatSnackBar,
              private readonly authenticationService: AuthenticationService,
              private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly toastr: ToastrService) {
  }

  ngOnInit(): void {
    this.currencyPairs = this.activatedRoute.parent.snapshot.data['preload'].currencyPairs;
    this.currencyPairs = [...this.currencyPairs.sort((a, b) => a.sixLetterCode.localeCompare(b.sixLetterCode))];
    this.initDatatableView();
    this.initUpdateForm();
  }

  reset() {
    this.updateForm.reset(this.currencyPairs);
  }

  initDatatableView() {
    this.columns = [
      {
        headerTemplate: this.sixLetterCodeHeaderTemplate,
        cellTemplate: this.sixLetterCodeColumnTemplate,
        name: 'Six letter code',
      },
      {
        headerTemplate: this.swapPriceFactorHeaderTemplate,
        cellTemplate: this.swapPriceFactorColumnTemplate,
        name: 'Swap price factor',
      },
      {
        headerTemplate: this.spotPriceFactorHeaderTemplate,
        cellTemplate: this.spotPriceFactorColumnTemplate,
        name: 'Spot price factor',
      },
      {
        headerTemplate: this.spotPricePrecisionHeaderTemplate,
        cellTemplate: this.spotPricePrecisionColumnTemplate,
        name: 'Spot price precision',
      },
      {
        headerTemplate: this.swapPricePrecisionHeaderTemplate,
        cellTemplate: this.swapPricePrecisionColumnTemplate,
        name: 'Swap price precision',
      },
      {
        headerTemplate: this.displaySpotPricePrecisionHeaderTemplate,
        cellTemplate: this.displaySpotPricePrecisionColumnTemplate,
        name: 'Display spot price precision',
      },
      {
        headerTemplate: this.roundingHeaderTemplate,
        cellTemplate: this.roundingColumnTemplate,
        name: 'Rounding',
      },
    ];
  }

  update() {
    this.restCallInProgress = true;
    const formValue = this.updateForm.getRawValue();
    const keys = Object.keys(formValue);
    const filtered = [];
    keys.forEach(k => {
      const item: FormGroup = this.updateForm.get(k) as FormGroup;
      if (item.dirty) {
        filtered.push(item.getRawValue());
      }
    });

    this.adminService.updateCurrencyPairs({updateCurrencyPairs: filtered}).subscribe({
      next: () => {
        this.updateForm.markAsPristine();
        this.toastr.success('Currency pair(s) updated');
      },
      error: (error) => {
        this.toastr.error(error.message);
      },
      complete: () => {
        this.restCallInProgress = false;
      }
    })
  }

  refresh() {
    this.bit = false;
    setTimeout(() => {
      this.bit = true;
    }, 10);
  }

  initUpdateForm() {
    this.updateForm = this.fb.group({});
    this.currencyPairs.forEach(cp => {
      this.addCurrencyPairToForm(cp);
    });
  }

  addCurrencyPairToForm(cp) {
    this.updateForm.addControl(cp.sixLetterCode, this.fb.group({
      sixLetterCode: new FormControl(cp.sixLetterCode, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
      swapPriceFactor: new FormControl(cp.swapPriceFactor, [Validators.required, UtilsService.factorValidator()]),
      spotPriceFactor: new FormControl(cp.spotPriceFactor, [Validators.required, UtilsService.factorValidator()]),
      spotPricePrecision: new FormControl(cp.spotPricePrecision, [Validators.required, Validators.min(0), Validators.max(12)]),
      swapPricePrecision: new FormControl(cp.swapPricePrecision, [Validators.required, Validators.min(0), Validators.max(12)]),
      displaySpotPricePrecision: new FormControl(cp.displaySpotPricePrecision, [Validators.required, Validators.min(0), Validators.max(12)]),
      rounding: new FormControl(cp.rounding, [Validators.required, UtilsService.roundingValidator()])
    }));
  }

  createCurrencyPairModal() {
    const modal = this.modalService.show(CreateCurrencyPairComponent, {
      class: 'modal-xxl'
    });
    modal.content.createEventEmitter.subscribe((value) => {
      this.restCallInProgress = true;
      const currencyPair = {
        ...value,
        swapPriceFactor: Number(value.swapPriceFactor),
        spotPriceFactor: Number(value.spotPriceFactor),
        spotPricePrecision: Number(value.spotPricePrecision),
        swapPricePrecision: Number(value.swapPricePrecision),
        displaySpotPricePrecision: Number(value.displaySpotPricePrecision),
        rounding: Number(value.rounding)
      }
      this.adminService.createCurrencyPair(currencyPair).subscribe({
        next: (resp: any) => {
          this.currencyPairs.push(resp);
          this.currencyPairs = [...this.currencyPairs.sort((a, b) => a.sixLetterCode.localeCompare(b.sixLetterCode))];
          this.addCurrencyPairToForm(resp);
          this.toastr.success('Currency pair created');
        }, error: (error) => {
          this.toastr.error(error.message);
        },
        complete: () => {
          this.restCallInProgress = false;
          modal.hide();
        }
      });
    });
  }
}
